package com.siriusxm.pia.views.unifiedaggregator.management

import kotlinx.serialization.json.*
import unifiedcontent.aggregator.AppConfigConstraintType
import kotlin.math.abs
import kotlin.math.floor

/*
 * Pure (Compose-free) logic behind the feature flag attribute editor (UCA-979).
 *
 * The composables in FeatureFlags.kt only render and forward input; every decision about which
 * widget to show, whether typed text is valid, how a value is displayed and what changed lives
 * here so that it can be unit tested on Node without a browser.
 */

/**
 * The widget used to edit a single feature flag attribute.
 */
enum class AttributeEditorKind {
    /** A single-line text field. Stores a JSON string. */
    Text,

    /** A single-line numeric field. Stores a JSON number. */
    Number,

    /** A checkbox. Stores a JSON boolean. */
    Boolean,

    /**
     * A JSON text area. Used when the declared type is missing or unrecognised, or when the
     * current value has a shape the typed widget cannot represent (an object, an array, or a
     * primitive of the wrong kind). This keeps every attribute visible and editable.
     */
    RawJson
}

/**
 * Identifies one attribute of one feature flag.
 */
data class AttributeRef(val flagId: String, val attrKey: String)

/**
 * The text an admin has typed into an attribute field, plus the validation error for that
 * text, if any. A draft with an error is never written into the pending updates.
 */
data class AttributeDraft(val text: String, val error: String?)

/**
 * The outcome of parsing the text typed into an attribute field.
 */
sealed interface AttributeParseResult {
    /** The text is valid and becomes [value]. */
    data class Valid(val value: JsonElement) : AttributeParseResult

    /** The text is not valid. [message] is shown under the field. */
    data class Invalid(val message: String) : AttributeParseResult
}

/**
 * One attribute whose value differs between the saved document and the pending update.
 * A null [old] means the attribute had no value before; a null [new] means it was removed.
 */
data class AttributeChange(val key: String, val old: JsonElement?, val new: JsonElement?)

/** The key the flag's on/off state is stored under, in the same object as its attributes. */
const val ENABLED_KEY = "enabled"

private val prettyJson = Json { prettyPrint = true }

/**
 * Chooses the editor for an attribute from its declared constraint type and the shape of its
 * saved value.
 *
 * The typed widget is used when the value is absent or null, or when it already matches the
 * declared type. Anything else falls back to [AttributeEditorKind.RawJson]:
 * - no type, or an [AppConfigConstraintType._Unknown] type the console predates;
 * - an object or array value;
 * - a primitive of the wrong kind (for example the string `"5"` on a `number` attribute),
 *   which a typed widget would either hide or silently convert.
 *
 * Callers should pass the *saved* value, not the edited one, so the widget does not switch
 * underneath the admin while they type.
 */
fun resolveAttributeEditorKind(type: AppConfigConstraintType?, savedValue: JsonElement?): AttributeEditorKind {
    val primitive = when (savedValue) {
        null, JsonNull -> null
        is JsonPrimitive -> savedValue
        else -> return AttributeEditorKind.RawJson
    }
    return when (type) {
        AppConfigConstraintType.STRING ->
            if (primitive == null || primitive.isString) AttributeEditorKind.Text else AttributeEditorKind.RawJson

        AppConfigConstraintType.NUMBER ->
            if (primitive == null || (!primitive.isString && primitive.doubleOrNull != null)) {
                AttributeEditorKind.Number
            } else {
                AttributeEditorKind.RawJson
            }

        AppConfigConstraintType.BOOLEAN ->
            if (primitive == null || (!primitive.isString && primitive.booleanOrNull != null)) {
                AttributeEditorKind.Boolean
            } else {
                AttributeEditorKind.RawJson
            }

        else -> AttributeEditorKind.RawJson
    }
}

/**
 * Parses the text typed into a [AttributeEditorKind.Text], [AttributeEditorKind.Number] or
 * [AttributeEditorKind.RawJson] field. Boolean attributes use a checkbox and are never parsed.
 *
 * - Text: any text is valid, including an empty string.
 * - Number: must be a finite decimal number. Whole numbers are stored as integers, so typing
 *   `360` or `360.0` both save `360` rather than `360.0`.
 * - Raw JSON: must be a single valid JSON value.
 */
fun parseAttributeInput(kind: AttributeEditorKind, text: String): AttributeParseResult {
    return when (kind) {
        AttributeEditorKind.Text -> AttributeParseResult.Valid(JsonPrimitive(text))
        AttributeEditorKind.Number -> parseNumber(text)
        AttributeEditorKind.RawJson -> parseRawJson(text)
        AttributeEditorKind.Boolean ->
            text.trim().toBooleanStrictOrNull()?.let { AttributeParseResult.Valid(JsonPrimitive(it)) }
                ?: AttributeParseResult.Invalid("Enter true or false")
    }
}

/** Strict decimal number syntax: optional sign, digits with optional fraction, optional exponent. */
private val numberPattern = Regex("""^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$""")

/** Whole numbers up to this size are stored exactly as integers (the largest exact JS integer). */
private const val MAX_EXACT_INTEGER = 9_007_199_254_740_991.0

private fun parseNumber(text: String): AttributeParseResult {
    val trimmed = text.trim()
    if (trimmed.isEmpty()) {
        return AttributeParseResult.Invalid("Enter a number")
    }
    // The regex rejects "NaN", "Infinity" and hex, which toDoubleOrNull would otherwise accept.
    val number = trimmed.takeIf { numberPattern.matches(it) }?.toDoubleOrNull()
    if (number == null || !number.isFinite()) {
        return AttributeParseResult.Invalid("\"$trimmed\" is not a number")
    }
    val value = if (floor(number) == number && abs(number) <= MAX_EXACT_INTEGER) {
        JsonPrimitive(number.toLong())
    } else {
        JsonPrimitive(number)
    }
    return AttributeParseResult.Valid(value)
}

private fun parseRawJson(text: String): AttributeParseResult {
    if (text.isBlank()) {
        return AttributeParseResult.Invalid("Enter a JSON value")
    }
    return try {
        AttributeParseResult.Valid(Json.parseToJsonElement(text))
    } catch (e: Exception) {
        // kotlinx.serialization appends the whole input after the first line; keep only the reason.
        val reason = e.message?.lineSequence()?.firstOrNull()?.takeIf { it.isNotBlank() } ?: "could not parse"
        AttributeParseResult.Invalid("Invalid JSON: $reason")
    }
}

/**
 * The text to show in an attribute's editor for [value].
 *
 * Strings show their content without quotes in the Text editor, numbers show their literal
 * form, and the raw JSON editor shows valid, pretty-printed JSON (so strings keep their
 * quotes there). An absent value shows as empty.
 */
fun attributeEditorText(kind: AttributeEditorKind, value: JsonElement?): String {
    if (value == null || value == JsonNull) {
        return if (kind == AttributeEditorKind.RawJson && value == JsonNull) "null" else ""
    }
    return when (kind) {
        AttributeEditorKind.RawJson -> prettyJson.encodeToString(JsonElement.serializer(), value)
        else -> (value as? JsonPrimitive)?.content ?: value.toString()
    }
}

/**
 * A compact, single-line rendering of any attribute value, for read-only summaries such as the
 * save confirmation dialog. Strings keep their quotes so `"5"` and `5` read differently.
 */
fun attributeSummaryText(value: JsonElement?): String = value?.toString() ?: "not set"

/**
 * Lists the attributes whose values differ between [saved] and [updated], ignoring the
 * `enabled` key. Keys are listed in [attributeOrder] first (the descriptor's order), then any
 * other changed keys alphabetically.
 */
fun attributeChanges(
    saved: JsonObject?,
    updated: JsonObject?,
    attributeOrder: List<String> = emptyList()
): List<AttributeChange> {
    val keys = ((saved?.keys ?: emptySet()) + (updated?.keys ?: emptySet())) - ENABLED_KEY
    val ordered = attributeOrder.filter { it in keys } + (keys - attributeOrder.toSet()).sorted()
    return ordered.mapNotNull { key ->
        val old = saved?.get(key)
        val new = updated?.get(key)
        if (old == new) null else AttributeChange(key, old, new)
    }
}

/**
 * Returns [updates] with the pending value for [flagId] set to [edited], or with the entry for
 * [flagId] removed when [edited] equals the saved value. This keeps `updates` holding only real
 * changes, so toggling a flag twice, or typing an attribute back to its saved value, leaves
 * nothing to save.
 */
fun withFlagEdit(
    updates: Map<String, JsonObject>,
    flagId: String,
    saved: JsonElement?,
    edited: JsonObject
): Map<String, JsonObject> {
    return if (edited == saved) updates - flagId else updates + (flagId to edited)
}
